import {
    Button,
    Divider,
    Flex,
    Image,
    Modal,
    ModalContent,
    ModalOverlay,
    Text,
    useColorMode,
    useDisclosure,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Keycloak from 'keycloak-js';
import { useContext, useEffect, useState } from 'react';
import BasicCurrencyIcon from '../../assets/basic_currency.svg';
import { AuthenticatedComponent } from '../../auth/keycloak';
import config from '../../config';
import PageDataContext from '../../context/PageDataContext';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import {
    createStreamsixSubscription,
    getProductByType,
    getUserSubscription,
    ProductDef,
    RealProduct,
    SubscriptionDef,
    WrapperLicense,
} from '../../service/inventory';
import { getUserPrivateProfile, UserProfile } from '../../service/social';
import { stripeAppearenceOptions } from '../../service/stripe';
import { CheckoutForm } from '../storeComponents/CheckoutForm';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe(config.stripePublicKey);

interface MembershipProps {
    keycloak: Keycloak;
}

export const Membership: AuthenticatedComponent<MembershipProps> = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const [subPackages, setPackages] = usePageDataCache<ProductDef[]>('subscriptionPackages', []);
    const [subscriptions, setSubscriptions] = usePageDataCache<{
        [producID: string]: SubscriptionDef;
    }>('membershipSubscription', {});
    const [clientSecret, setClientSecret] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductDef | undefined>();
    const [myProfile, setMyProfile] = useSessionStorage<UserProfile | undefined>(
        'myProfile',
        undefined,
    );
    const localStoragePrefix = 'Membership';

    const { isOpen: isSubOpen, onOpen: onSubOpen, onClose: onSubClose } = useDisclosure();

    const { colorMode } = useColorMode();

    const getPackages = async () => {
        setPackages([]);
        try {
            const token = keycloak.token;
            const packages = await getProductByType(RealProduct, WrapperLicense, token || '');
            const currency = [];
            for (let i = 0; i < packages.length; i++) {
                for (let j = 0; j < packages[i].items.length; j++) {
                    const item = packages[i].items[j].item_def;
                    if (item && item.item_type === 'membership_sub' && item.is_subscription) {
                        currency.push(packages[i]);
                        break;
                    }
                }
            }
            setPackages(currency);
            getSubscription(currency);
        } catch (err: any) {
            console.error(err);
            setPackages([]);
        }
    };

    const getSubscription = async (currency: ProductDef[]) => {
        setSubscriptions({});
        try {
            const subs: { [producID: string]: SubscriptionDef } = {};
            for (let i = 0; i < currency.length; i++) {
                for (let j = 0; j < currency[i].items.length; j++) {
                    if (currency[i].items[j].item_def?.is_subscription) {
                        const def = currency[i].items[j].item_def;
                        if (def) {
                            const userSubs = await getUserSubscription(
                                keycloak.subject,
                                'streamsix',
                                def.item_id,
                                keycloak.token,
                            );
                            if (userSubs.length > 0) {
                                subs[currency[i].product_id] = userSubs[0];
                            }
                        }
                    }
                }
            }
            setSubscriptions(subs);
        } catch (err: any) {
            console.error(err);
            setSubscriptions({});
        }
    };

    const hasSubscription = (productID: string): boolean => {
        return subscriptions[productID] !== undefined;
    };

    const getErrorMsg = (productID: string): string => {
        const sub = subscriptions[productID];
        if (!sub || !sub.status || !sub.status.payload) {
            return '';
        }

        if (sub.status.payload.last_payment_error) {
            return sub.status.payload.last_payment_error.message;
        }
        if (sub.status.payload.last_finalization_error) {
            return sub.status.payload.last_finalization_error.message;
        }

        return '';
    };

    useEffect(() => {
        if (!isLoadFromCache) {
            getPackages();

            async function getProfileInfo() {
                const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
                setMyProfile(profile);
            }
            if (myProfile === undefined) {
                getProfileInfo();
            }
        }
    }, []);

    const onSubClick = async (product: ProductDef) => {
        const intent = await createStreamsixSubscription(product.product_id, keycloak.token || '');
        setClientSecret(intent.client_secret);
        setSelectedProduct(product);
        onSubOpen();
    };

    const onSubManageClick = () => {
        window.open(config.stripeCustomerPortalURL, '_blank');
    };

    const appearance = stripeAppearenceOptions(colorMode);

    const elementOptions = {
        clientSecret,
        appearance,
    };

    const renderModal = () => (
        <Modal
            closeOnOverlayClick={false}
            isOpen={isSubOpen}
            onClose={onSubClose}
            isCentered
            scrollBehavior={'inside'}
        >
            <ModalOverlay className="modal-overlay" />
            <ModalContent className="modal-content">
                {clientSecret && (
                    <Elements options={elementOptions} stripe={stripePromise}>
                        <CheckoutForm
                            clientSecret={clientSecret}
                            keycloak={keycloak}
                            product={selectedProduct}
                            email={myProfile?.email || ''}
                            onFormClose={(err) => {
                                onSubClose();
                                getPackages();
                            }}
                        />
                    </Elements>
                )}
            </ModalContent>
        </Modal>
    );

    return (
        <>
            {renderModal()}
            <Flex className="membership-items">
                {subPackages.map((pkg, i) => (
                    <Flex key={i} className={'subscription-plan'}>
                        <Image src={BasicCurrencyIcon} />
                        <Text>{`${pkg.product_name}`}</Text>
                        {!hasSubscription(pkg.product_id) && (
                            <Button
                                isDisabled={keycloak.hasResourceRole('disable_purchase', 'realm')}
                                onClick={() => {
                                    onSubClick(pkg);
                                }}
                            >
                                Sub Now
                            </Button>
                        )}
                        {hasSubscription(pkg.product_id) && (
                            <Button
                                onClick={() => {
                                    onSubManageClick();
                                }}
                            >
                                {`${
                                    getErrorMsg(pkg.product_id) !== ''
                                        ? getErrorMsg(pkg.product_id)
                                        : 'Manage'
                                }`}
                            </Button>
                        )}
                    </Flex>
                ))}
                <Divider className="divider" />
            </Flex>
        </>
    );
};
